import { BOT_DIFFICULTIES, type BotDifficulty } from "@bing/shared";
import type { ReactNode } from "react";
import { fa } from "../i18n/fa";

/** Persian label per difficulty level (آسان / متوسط / سخت). */
const LABELS: Readonly<Record<BotDifficulty, string>> = {
  easy: fa.difficulty.easy,
  medium: fa.difficulty.medium,
  hard: fa.difficulty.hard,
};

/**
 * A compact segmented single-select for the AI opponent difficulty — the same
 * gold-on-felt segmented radiogroup pattern used by the Settings «پوسته» and the
 * GameSettings "turn timer" controls, extracted here as ONE shared control so the
 * solo-start flow and the private-table settings share it (no duplicated primitive).
 * Controlled: the caller owns the value + persists it (`setBotDifficulty`).
 */
export function DifficultySelect({
  value,
  onChange,
  ariaLabel = fa.difficulty.labelMain,
  levels = BOT_DIFFICULTIES,
  labels = LABELS,
  compact = false,
}: {
  value: BotDifficulty;
  onChange: (value: BotDifficulty) => void;
  /** Accessible name for the group (defaults to the difficulty label). */
  ariaLabel?: string;
  /**
   * WHICH levels this game offers. Defaults to all three.
   *
   * ⚠️ NOT EVERY GAME HAS THREE. منچ has two — one die and at most four candidate tokens leave
   * 80.3% of its turns with no choice at all, so a third tier could only ever be a relabelling of
   * one of the others (it WAS exactly that, and measured 50.0%). A game passes the levels it can
   * genuinely tell apart, rather than the control implying a rung that is not there.
   */
  levels?: readonly BotDifficulty[];
  /** Per-level labels, when a game names its levels differently (منچ: «معمولی» / «سخت»). */
  labels?: Readonly<Record<BotDifficulty, string>>;
  /** Tighter padding for a start screen, where every row competes for a measured height budget. */
  compact?: boolean;
}): ReactNode {
  return (
    <div
      role="radiogroup"
      aria-label={ariaLabel}
      className={`flex gap-1 rounded-lg bg-surface-container-highest ${compact ? "p-0.5" : "p-1"}`}
    >
      {levels.map((level) => {
        const on = value === level;
        return (
          <button
            key={level}
            type="button"
            role="radio"
            aria-checked={on}
            aria-label={labels[level]}
            onClick={() => onChange(level)}
            className={`flex-1 appearance-none cursor-pointer whitespace-nowrap rounded-md border-0 ${
              compact ? "py-2" : "py-2.5"
            } text-center font-body-md text-body-md transition-colors ${
              on
                ? "bg-gold-deep text-ink-on-gold shadow"
                : "bg-transparent text-on-surface-variant hover:text-on-surface"
            }`}
          >
            {labels[level]}
          </button>
        );
      })}
    </div>
  );
}
